import { Popup } from '@paywithextend/components'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ChurnSurveyScreen from 'src/component-modal/ChurnSurveyModal/ChurnSurveyModal'
import { useChurnSurveySubmit } from 'src/component-modal/ChurnSurveyModal/useChurnSurveySubmit'
import CancelSubscriptionInfo from 'src/components/CancelSubscription/CancelSubscriptionInfo'
import CancelSubscriptionSubmission from 'src/components/CancelSubscription/CancelSubscriptionSubmission'
import { connectedReplace } from 'src/navigation/Lnk'
import { manageSubscriptionUrl } from 'src/navigation/urls'
import {
  useCancelSubscription,
  useQueryOrganizationSaasTier,
} from 'src/queries/organizations/saas.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

export default function CancelSubscriptionPage(): ReactElement {
  const dispatch = useDispatch()
  const id = useSelector(selectUserListModelFromState)?.organizationId
  const { analytics } = useAnalyticsContext()

  const { mutate: cancelSubscription } = useCancelSubscription()
  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: orgId || '',
    enabled: !!orgId,
  })

  type CancelSubscriptionScreen = 'info' | 'submission' | 'survey'
  const [currentScreen, setCurrentScreen] =
    useState<CancelSubscriptionScreen>('info')

  const leaveCancelSubscriptionModal = useCallback((): void => {
    if (id) {
      dispatch(connectedReplace(manageSubscriptionUrl(id)))
    }
  }, [id, dispatch])

  const handleSurveySubmit = useChurnSurveySubmit(orgId)

  useEffect(() => {
    switch (currentScreen) {
      case 'info':
        analytics.track('cancel subscription', { action: 'info_viewed' })
        break
      case 'submission':
        analytics.track('cancel subscription', { action: 'submission_viewed' })
        break
      case 'survey':
        analytics.track('churn survey', { action: 'viewed' })
        break
    }
  }, [currentScreen, analytics])

  const handleAbandon = useCallback(
    (step: 'info' | 'submission'): void => {
      analytics.track('cancel subscription', {
        action: 'abandoned',
        abandon_step: step,
      })
      leaveCancelSubscriptionModal()
    },
    [analytics, leaveCancelSubscriptionModal],
  )

  const handlePopupClose = useCallback((): void => {
    switch (currentScreen) {
      case 'info':
        handleAbandon('info')
        break
      case 'submission':
        handleAbandon('submission')
        break
      case 'survey':
        leaveCancelSubscriptionModal()
        break
    }
  }, [currentScreen, handleAbandon, leaveCancelSubscriptionModal])

  const handleConfirmDowngrade = useCallback((): void => {
    if (!orgId) return
    analytics.track('cancel subscription', {
      action: 'confirmed',
      tier: currentPlan?.tier,
    })
    cancelSubscription({
      orgId,
      tier: currentPlan?.tier,
      body: {
        intention: 'cancel_subscription',
      },
    })
    setCurrentScreen('survey')
  }, [orgId, analytics, currentPlan, cancelSubscription])

  const currentScreenComponent = useMemo(() => {
    switch (currentScreen) {
      case 'info':
        return (
          <CancelSubscriptionInfo
            onConfirm={(): void => setCurrentScreen('submission')}
            onCancel={(): void => handleAbandon('info')}
          />
        )
      case 'submission':
        return (
          <CancelSubscriptionSubmission
            onConfirm={handleConfirmDowngrade}
            onCancel={(): void => handleAbandon('submission')}
          />
        )
      case 'survey':
        return (
          <ChurnSurveyScreen
            onSubmit={handleSurveySubmit}
            onSkip={leaveCancelSubscriptionModal}
          />
        )
    }
  }, [
    currentScreen,
    handleConfirmDowngrade,
    handleSurveySubmit,
    handleAbandon,
    leaveCancelSubscriptionModal,
  ])
  return (
    <Popup
      showCloseButton
      onClickClose={handlePopupClose}
      css={css`
        overflow: clip;
      `}
    >
      {currentScreenComponent}
    </Popup>
  )
}
